import { FC } from 'react';
import { Stack, Group, Box, Button, Input } from '@mantine/core';
import { ResourceInput } from '@medplum/react';
import { Practitioner } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';

// -----------------------------------------------------------------------------
interface AdditionalDoctorsListProps {
  doctors: Practitioner[];
  onChange: (doctors: Practitioner[]) => void;
  label?: string;
  required?: boolean;
}

// -----------------------------------------------------------------------------
export const AdditionalDoctorsList: FC<AdditionalDoctorsListProps> = ({
  doctors,
  onChange,
  label,
  required = false,
}) => {
  const { t } = useTranslation(['encounters']);

  // ---------------------------------------------------------------------------
  const handleAdd = () => {
    onChange([...doctors, {} as Practitioner]);
  };

  // ---------------------------------------------------------------------------
  const handleRemove = (index: number) => {
    const newDoctors = doctors.filter((_, i) => i !== index);
    onChange(newDoctors);
  };

  // ---------------------------------------------------------------------------
  const handleUpdate = (index: number, value: Practitioner | undefined) => {
    const newDoctors = [...doctors];
    if (value) {
      newDoctors[index] = value;
    } else {
      newDoctors.splice(index, 1);
    }

    onChange(newDoctors);
  };

  // ---------------------------------------------------------------------------
  return (
    <Stack gap="xs">
      <Input.Label required={required}>{label}</Input.Label>

      {doctors.map((doctor, index) => (
        <Group key={doctor.id || index} align="center">
          <Box flex={1}>
            <ResourceInput<Practitioner>
              resourceType="Practitioner"
              name={`additionalDoctor-${index}`}
              defaultValue={doctor}
              onChange={(value) =>
                handleUpdate(index, value as Practitioner | undefined)
              }
            />
          </Box>
          <Button
            variant="subtle"
            color="red"
            size="sm"
            onClick={() => handleRemove(index)}
          >
            {t('removeDoctor', { ns: 'encounters' })}
          </Button>
        </Group>
      ))}

      <Button variant="light" size="sm" onClick={handleAdd} fullWidth={false}>
        {t('addDoctor', { ns: 'encounters' })}
      </Button>
    </Stack>
  );
};
